// The host's level change during a game: pick a game and a level, and the server moves everyone there (another game
// is loaded by everyone, as at the start). The level lists come from the server ("stages"), one request per game.

import type { Room } from "colyseus.js";
import type { GameId, LobbyStage } from "@sonic-coop/engine";

const GAME_NAMES: Record<string, string> = { sonic1: "Sonic 1", sonic2: "Sonic 2", soniccd: "Sonic CD" };

export class LevelPicker {
  readonly root = document.createElement("form");
  private game = document.createElement("select");
  private stage = document.createElement("select");
  private lists = new Map<GameId, LobbyStage[]>();
  private unsubscribe: () => void;
  isOpen = false;

  constructor(private room: Room, parent: HTMLElement, games: GameId[], current: GameId, private onOpenChange: (open: boolean) => void) {
    this.root.className = "level-picker";
    this.root.hidden = true;
    const title = document.createElement("h2");
    title.textContent = "Change level";
    for (const g of games.filter((g) => g !== "sonic3")) this.game.append(new Option(GAME_NAMES[g] ?? g, g));
    this.game.value = current;
    const go = document.createElement("button");
    go.textContent = "Go";
    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancel";
    cancel.onclick = () => this.close();
    const label = (text: string, input: HTMLElement) => {
      const l = document.createElement("label");
      l.append(text, input);
      return l;
    };
    const row = document.createElement("div");
    row.className = "row";
    row.append(go, cancel);
    const hint = document.createElement("small");
    hint.textContent = "Everyone goes there. Another game loads for everyone first.";
    this.root.append(title, label("Game", this.game), label("Level", this.stage), hint, row);
    parent.append(this.root);
    // menus must not steer the character, and Escape closes
    this.root.addEventListener("keydown", (e) => {
      e.stopPropagation();
      if (e.key === "Escape") this.close();
    });
    this.game.onchange = () => this.showStages();
    this.root.onsubmit = (e) => {
      e.preventDefault();
      if (!this.stage.options.length) return;
      this.room.send("lobby", { changeLevel: { game: this.game.value as GameId, stage: Number(this.stage.value) } });
      this.close();
    };
    this.unsubscribe = room.onMessage("stages", (m: { game: GameId; stages: LobbyStage[] }) => {
      this.lists.set(m.game, m.stages);
      if (m.game === this.game.value) this.showStages();
    });
  }

  private showStages(): void {
    const game = this.game.value as GameId;
    const list = this.lists.get(game);
    if (!list) {
      this.stage.replaceChildren(new Option("Loading…", ""));
      this.room.send("stages", game);
      return;
    }
    this.stage.replaceChildren(...list.map((s, i) => new Option(`${s.level} - ${s.act}`, String(i))));
  }

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.root.hidden = false;
    this.showStages();
    this.game.focus();
    this.onOpenChange(true);
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.root.hidden = true;
    this.onOpenChange(false);
  }

  dispose(): void {
    this.close();
    this.unsubscribe();
    this.root.remove();
  }
}
